import React from 'react';
import { NewsCard } from '@/components';
import { useNewsData } from '@/hooks/useNewsData';
import type { NewsItem } from '@/types/newsType';

interface NewsSectionProps {
  onNewsClick: (newsId: string) => void;
}

const NewsSection: React.FC<NewsSectionProps> = ({ onNewsClick }) => {
  const { newsItems, loading, error } = useNewsData();


  // Loading state
  if (loading) {
    return (
      <section className="flex flex-col gap-6 mt-12 md:mt-14 mb-12 md:mb-14" aria-labelledby="news-title">
        <h1 id="news-title" className="text-2xl md:text-3xl lg:text-4xl font-medium text-black">Tin nổi bật</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Loading skeletons */}
          {[...Array(6)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-300 rounded-lg h-48 mb-4"></div>
              <div className="bg-gray-300 h-4 rounded mb-2"></div>
              <div className="bg-gray-300 h-3 rounded mb-1"></div>
              <div className="bg-gray-300 h-3 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="flex flex-col gap-6 mt-12 md:mt-14 mb-12 md:mb-14" aria-labelledby="news-title">
        <h1 id="news-title" className="text-2xl md:text-3xl lg:text-4xl font-medium text-black">Tin nổi bật</h1>
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">Không thể tải tin tức</p>
            <p className="text-sm text-gray-600 mt-2">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  // Success state - show news items
  return (
    <section className="flex flex-col gap-6 mt-12 md:mt-14 mb-12 md:mb-14" aria-labelledby="news-title">
      <h1 id="news-title" className="text-2xl md:text-3xl lg:text-4xl font-medium text-black">Tin nổi bật</h1>
      
      {newsItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Không có tin tức nào để hiển thị</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsItems.map((item: NewsItem) => (
            <NewsCard
              key={item.id}
              imageUrl={item.img?.url || ''}
              imageAlt={item.img?.alt || ''}
              title={item.title}
              description={item.content.html}
              onClick={() => onNewsClick(item.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default NewsSection;
